import { Box, Typography } from "@mui/material";
import DefaultButton from "../general/buttons/DefaultButton";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import React, { useEffect, useState } from "react";
import { MusicProvider } from "../../services/musicProvider";
import { connectApple, connectSpotify, disconnectProvider, fetchProviderStatus, ProviderStatus } from "../../services/providerConnect";
import { colors } from "../../theme";

interface SupportedApp {
    name: MusicProvider | "YouTube Music";
    iconUrl: string;
    available: boolean;
}

const supportedApps: SupportedApp[] = [
    { name: "Spotify", iconUrl: "/assets/spotify_icon.png", available: true },
    { name: "Apple Music", iconUrl: "/assets/apple_music_icon.png", available: true },
    { name: "YouTube Music", iconUrl: "/assets/youtube_icon.png", available: false },
];

interface AppleConfig {
    configured: boolean;
}

const EMPTY_STATUS: ProviderStatus = { connected: false, username: "" };

export default function MainPanel() {
    const [spotifyStatus, setSpotifyStatus] = useState<ProviderStatus>(EMPTY_STATUS);
    const [appleStatus, setAppleStatus] = useState<ProviderStatus>(EMPTY_STATUS);
    const [appleConfig, setAppleConfig] = useState<AppleConfig>({ configured: true });
    const [connectingApple, setConnectingApple] = useState(false);
    const [disconnecting, setDisconnecting] = useState<MusicProvider | null>(null);
    const [error, setError] = useState<Partial<Record<MusicProvider, string>>>({});

    const refreshSpotifyStatus = () => {
        fetchProviderStatus("Spotify").then(setSpotifyStatus).catch(() => setSpotifyStatus(EMPTY_STATUS));
    };

    const refreshAppleStatus = () => {
        fetchProviderStatus("Apple Music").then(setAppleStatus).catch(() => setAppleStatus(EMPTY_STATUS));
        // /v1/apple/status also carries whether the server has Apple Music configured
        // at all (team id / key on file) - fetchProviderStatus only surfaces `connected`,
        // so this is queried separately to drive the "support coming soon" fallback.
        fetch(`${process.env.REACT_APP_API_URL || ""}/v1/apple/status`, { credentials: "include" })
            .then(r => r.json())
            .then(data => setAppleConfig({ configured: !!data.configured }))
            .catch(() => setAppleConfig({ configured: false }));
    };

    useEffect(() => {
        refreshSpotifyStatus();
        refreshAppleStatus();
    }, []);

    const handleAppleConnect = async () => {
        setConnectingApple(true);
        setError(prev => ({ ...prev, "Apple Music": undefined }));
        try {
            // Opens Apple's own sign-in UI - the user completes that themselves.
            await connectApple();
            refreshAppleStatus();
        } catch (err: any) {
            setError(prev => ({ ...prev, "Apple Music": err?.message || "Couldn't connect Apple Music" }));
        } finally {
            setConnectingApple(false);
        }
    };

    const handleConnect = (provider: MusicProvider) => {
        if (provider === "Spotify") {
            connectSpotify(true);
        } else {
            handleAppleConnect();
        }
    };

    const handleDisconnect = async (provider: MusicProvider) => {
        setDisconnecting(provider);
        setError(prev => ({ ...prev, [provider]: undefined }));
        try {
            await disconnectProvider(provider);
            if (provider === "Spotify") {
                refreshSpotifyStatus();
            } else {
                refreshAppleStatus();
            }
        } catch (err: any) {
            setError(prev => ({ ...prev, [provider]: err?.message || `Couldn't disconnect ${provider}` }));
        } finally {
            setDisconnecting(null);
        }
    };

    return (
        <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 }, maxWidth: 860 }}>
            <Typography variant="h3" sx={{ mb: 1 }}>Connected Apps</Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary, mb: 5 }}>
                Connect a streaming service so Shufl can sync shared playlists into your own library.
            </Typography>

            <Box
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${colors.line}`,
                    backgroundColor: colors.elevated,
                    overflow: "hidden",
                }}
            >
                {supportedApps.map((app, i) => {
                    const isApple = app.name === "Apple Music";
                    const status = app.name === "Spotify" ? spotifyStatus : isApple ? appleStatus : EMPTY_STATUS;
                    // Apple Music is "available" in the UI, but if the backend itself
                    // has no key configured, connecting can't actually work yet.
                    const available = isApple ? app.available && appleConfig.configured : app.available;
                    const appError = app.name !== "YouTube Music" ? error[app.name] : undefined;

                    return (
                        <Box
                            key={app.name}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2.5,
                                px: 3,
                                py: 2.5,
                                borderTop: i === 0 ? "none" : `1px solid ${colors.line}`,
                            }}
                        >
                            <Box
                                component="img"
                                src={app.iconUrl}
                                onError={e => { e.currentTarget.src = "/assets/artist_example.png"; }}
                                alt=""
                                sx={{ width: 48, height: 48, borderRadius: 1.5, objectFit: "contain", flexShrink: 0 }}
                            />

                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="h6">{app.name}</Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: status.connected ? colors.accent : colors.textMuted,
                                        fontWeight: 600,
                                    }}
                                >
                                    {status.connected
                                        ? (status.username ? `Connected as ${status.username}` : "Connected")
                                        : available ? "Not connected" : "Support coming soon"}
                                </Typography>
                                {appError && (
                                    <Typography sx={{ fontSize: 12, color: colors.danger, mt: 0.5 }}>
                                        {appError}
                                    </Typography>
                                )}
                            </Box>

                            {status.connected ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <CheckCircleIcon sx={{ color: colors.accent, fontSize: 28 }} />
                                    <DefaultButton
                                        text={disconnecting === app.name ? "Disconnecting…" : "Disconnect"}
                                        variant="secondary"
                                        size="small"
                                        disabled={disconnecting === app.name}
                                        onClick={() => handleDisconnect(app.name as MusicProvider)}
                                    />
                                </Box>
                            ) : (
                                <DefaultButton
                                    text={isApple && connectingApple ? "Connecting…" : "Connect"}
                                    variant={available ? "primary" : "secondary"}
                                    size="small"
                                    disabled={!available || (isApple && connectingApple)}
                                    onClick={() => handleConnect(app.name as MusicProvider)}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
